import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { sortBy } from 'lodash';
import {
    Box,
    Button,
    Grid,
    Stack
} from '@mui/material';
import {
    Edit,
    LocalShipping,
    Construction,
    CardMembership,
    Forest,
    CorporateFare,
    Domain,
    Map as MapIcon
} from '@mui/icons-material';

import {
    Block,
    List,
    Popup,
    SectionDivider,
    ToggleCheckbox
} from '@commonComponents';
import { acceptTenant, rejectTenant } from '@api-admin/tenants';
import {
    applyScrapedCompanies,
    enqueueTenantScrape,
    getTenantScrapedCompany
} from '@api-admin/scraped-companies';
import { TenantType } from '@types';
import toast from '@utils/toast';
import {
    formatBool,
    formatDateTime,
    formatNumber,
    formatPhone,
    formatFairtransportMembership,
    formatExternalUrl
} from '@utils/parsing';
import { FetchPropsType } from '@commonComponents/Fetch/FetchPropsType';
import { fetchTenantById, selectFetchingTenantById, selectTenantById } from '@actions/tenants';
import FetchRedux from '@commonComponents/Fetch/FetchRedux';
import { RowTypeType } from '@commonComponents/List';
import { websiteUrl, carrierNameCapitalized, nonCarrierNameCapitalized } from '@env';
import { icons } from '@theme';
import { jobLabel, ScrapedCompanyItem } from '../ScrapedCompanies/format';
import ScrapedChanges from '../ScrapedCompanies/ScrapedChanges';
import Logotype from './LogotypeComponents/Logotype';
import SubscriptionEditor from './SubscriptionEditor';
import TenantRecommendations from './TenantRecommendations';

const allaBolagTooltip = {
    icon: <icons.allabolag style={{ width: 16, height: 16, color: 'grey' }} />,
    title: 'Information hämtad från allabolag.se'
};

const fairtransportTooltip = {
    icon: <icons.fairtransport style={{ width: 16, height: 16, color: 'grey' }} />,
    title: 'Information hämtad från fairtransport.se'
};

function TenantDetails(props: FetchPropsType<TenantType>) {
    const { entity, fetch } = props;
    const navigate = useNavigate();
    const [scrapedItem, setScrapedItem] = React.useState<ScrapedCompanyItem | null>(null);
    const [confirmMode, setConfirmMode] = React.useState<'overwrite' | 'unedited' | null>(null);

    const loadScraped = React.useCallback(async () => {
        if (entity.isRejected) {
            setScrapedItem(null);
            return;
        }
        try {
            const data = await getTenantScrapedCompany(entity.id);
            setScrapedItem(data.item);
        } catch (error) {
            console.error(error);
        }
    }, [entity.id, entity.isRejected]);

    React.useEffect(() => {
        loadScraped();
    }, [loadScraped]);

    const queueScrape = React.useCallback(async () => {
        try {
            const result = await enqueueTenantScrape(entity.id);
            toast(result.status === 'alreadyPending' ? 'Redan köad' : 'Köad');
            loadScraped();
        } catch (error) {
            toast(error, 'Kunde inte köa skrapning');
        }
    }, [entity.id, loadScraped]);

    const setAccepted = React.useCallback(async (isAccepted) => {
        await acceptTenant(entity.id, isAccepted);
        fetch();
        toast(`Företaget ${isAccepted ? '' : 'ej'} accepterat`);
    }, [entity.id, fetch]);

    const setRejected = React.useCallback(async (isRejected) => {
        await rejectTenant(entity.id, isRejected);
        fetch();
        toast(`Företaget ${isRejected ? '' : 'ej'} avvisat`);
    }, [entity.id, fetch]);

    const data = [
        {
            name: 'Namn',
            value: entity.name
        }, {
            name: 'Org. nummer',
            value: entity.orgNumber
        }, {
            name: 'Typ',
            value: entity.isCarrier ? carrierNameCapitalized : nonCarrierNameCapitalized
        }, {
            name: 'Accepterat',
            type: 'raw' as RowTypeType,
            valuePadding: 1,
            value: <ToggleCheckbox
                value={entity.isAccepted}
                onToggle={setAccepted}
                confirm
                confirmText={`Vill du verkligen ${entity.isAccepted ? 'av-' : ''}acceptera ${entity.name || 'företaget'}?`}
            />
        }, {
            name: 'Avvisat',
            type: 'raw' as RowTypeType,
            valuePadding: 1,
            value: <ToggleCheckbox
                value={entity.isRejected}
                onToggle={setRejected}
                checkedColor="error"
                confirm
                confirmText={`Vill du verkligen ${entity.isRejected ? 'av-' : ''}avvisa ${entity.name || 'företaget'}?`}
            />
        }, {
            name: 'Abonnemang',
            type: 'raw' as RowTypeType,
            valuePadding: 1,
            value: (
                <SubscriptionEditor
                    entity={entity}
                    onSaved={fetch}
                />
            ),
            isHidden: !entity.isCarrier
        }, {
            name: 'Omsättning',
            value: entity.revenue * 1000,
            type: 'currency' as RowTypeType
        }, {
            name: 'Antal anställda',
            value: entity.employees,
            type: 'number' as RowTypeType
        }, {
            name: 'E-post',
            value: entity.email,
            type: 'email' as RowTypeType
        }, {
            name: 'Telefon',
            value: formatPhone(entity.phone),
            type: 'phone' as RowTypeType
        }, {
            name: 'Webbsida',
            value: entity.website
                ? `${formatExternalUrl(entity.website)}`
                : '',
            type: 'website' as RowTypeType
        }, {
            name: 'Landningssida',
            value: entity.slug
                ? `${formatExternalUrl(`${websiteUrl}/foretag/${entity.slug}`)}`
                : '',
            type: 'website' as RowTypeType,
            conditional: 'slug',
            isHidden: !entity.isCarrier || entity.isRejected
        }, {
            name: 'Slug',
            value: entity.slug ?? '',
            conditional: 'slug',
            isHidden: !entity.isCarrier || entity.isRejected
        }, {
            name: 'Adress',
            value: entity.street ?? ''
        }, {
            name: 'Postnummer',
            value: entity.zip ?? ''
        }, {
            name: 'Ort',
            value: entity.city ?? ''
        }, {
            name: 'Koordinater',
            value: (entity.lat && entity.lng)
                ? `${entity.lat} ${entity.lng}`
                : ''
        }, {
            name: 'Faktura, e-post',
            value: entity.billingEmail ?? '',
            type: 'email' as RowTypeType
        }, {
            name: 'Faktura, märkning',
            value: entity.billingMark ?? ''
        }, {
            name: 'Faktura, address',
            value: entity.billingStreet ?? ''
        }, {
            name: 'Faktura, postnummer',
            value: entity.billingZip ?? ''
        }, {
            name: 'Faktura, ort',
            value: entity.billingCity ?? ''
        }, {
            name: 'Skapat',
            value: entity.createdAt
                ? `${formatDateTime(entity.createdAt)} ${entity.isExternallyCreated ? 'EXTERNT' : 'INTERNT'}`
                : ''
        }, {
            name: 'Uppdaterat',
            value: entity.updatedAt
                ? `${formatDateTime(entity.updatedAt)} av ${entity.updatedByName || '-'}`
                : '',
            url: entity.updatedBy ? `/users/${entity.updatedBy}` : undefined
        }];

    const scrapedData = [
        {
            name: 'Allabolag',
            infoTooltip: allaBolagTooltip,
            value: entity.allabolagUrl
                ? `${formatExternalUrl(`www.allabolag.se/${entity.allabolagUrl}`)}`
                : '',
            type: 'website' as RowTypeType
        }, {
            name: 'Resultat efter finansnetto',
            infoTooltip: allaBolagTooltip,
            value: entity.profitAfterFinancialItems
                ? formatNumber(entity.profitAfterFinancialItems, 'SEK')
                : ''
        }, {
            name: 'Årets resultat',
            infoTooltip: allaBolagTooltip,
            value: entity.netResult
                ? formatNumber(entity.netResult, 'SEK')
                : ''
        }, {
            name: 'Bolagsform',
            infoTooltip: allaBolagTooltip,
            value: entity.legalEntity || ''
        }, {
            name: 'Kassalikviditet',
            infoTooltip: allaBolagTooltip,
            value: entity.cashRatio
                ? formatNumber(entity.cashRatio, 'SEK')
                : ''
        }, {
            name: 'Vinstmarginal',
            infoTooltip: allaBolagTooltip,
            value: entity.profitMargin
                ? formatNumber(entity.profitMargin, '%')
                : ''
        }, {
            name: 'Soliditet',
            infoTooltip: allaBolagTooltip,
            value: entity.equityRatio
                ? formatNumber(entity.equityRatio, '%')
                : ''
        }, {
            name: 'Bolagets status',
            infoTooltip: allaBolagTooltip,
            value: entity.companyStatus || ''
        }, {
            name: 'Momsregistrerad',
            infoTooltip: allaBolagTooltip,
            value: formatBool(entity.vat)
        }, {
            name: 'F-skatt',
            infoTooltip: allaBolagTooltip,
            value: formatBool(entity.fTax)
        }, {
            name: 'Arbetsgivaravgift',
            infoTooltip: allaBolagTooltip,
            value: formatBool(entity.employerContributions)
        }, {
            name: 'Fair Transport-sida',
            infoTooltip: fairtransportTooltip,
            value: entity.fairtransportUrl
                ? `${formatExternalUrl(entity.fairtransportUrl)}`
                : '',
            type: 'website' as RowTypeType
        }, {
            name: 'Fair Transport-medlemskap',
            infoTooltip: fairtransportTooltip,
            value: formatFairtransportMembership(entity.fairtransportMembership)
        }, {
            name: 'Fair Transport epost',
            infoTooltip: fairtransportTooltip,
            value: entity.fairtransportEmail || '',
            type: 'email' as RowTypeType
        }, {
            name: 'Fair Transport telefon',
            infoTooltip: fairtransportTooltip,
            value: formatPhone(entity.fairtransportPhone),
            type: 'phone' as RowTypeType
        }, {
            name: 'Antal lastbilar',
            value: entity.numberOfTrucks != null ? entity.numberOfTrucks : '',
            type: 'number' as RowTypeType
        }, {
            name: 'Skrapad senast',
            value: entity.scrapedAt
                ? `${formatDateTime(entity.scrapedAt)}`
                : '-'
        }];

    const licenceCertificatesCount = (entity.licencesCertificates?.length || 0)
        + (entity.customLicencesCertificates?.length || 0);

    const allLicencesCertificates = [...(entity.licencesCertificates || []), ...(entity.customLicencesCertificates || [])];

    const BasicInfoBlock = (
        <Block
            title="Uppgifter"
            variant="outlined"
            ActionComponent={(
                <Button
                    variant="outlined"
                    startIcon={<Edit />}
                    onClick={() => navigate(`/tenants/edit/${entity.id}`)}
                    sx={{ ml: 1, mb: 1 }}
                >
                    Redigera
                </Button>
            )}
        >
            <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ px: 2, pt: 2, pb: 1 }}>
                <Logotype
                    url={entity.logoUrl}
                    name={entity.name}
                    size={72}
                />
                <Box>
                    <Box sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                        {entity.name}
                    </Box>
                    <Box sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                        {entity.orgNumber}
                    </Box>
                </Box>
            </Stack>
            <List
                data={data}
            />
            <Grid item xs={12} px={2} pb={2}>
                <SectionDivider label="Presentation" marginTop={2} marginBottom={0} />
                <Box sx={{ px: 2, mt: 1, mb: 0 }}>
                    {entity.description || 'Ingen beskrivning inlagd.'}
                </Box>
            </Grid>
        </Block>
    );

    const ScrapedDataBlock = (
        <Block
            title="Skrapad data"
            variant="outlined"
        >
            <List
                data={scrapedData}
            />
            <Grid item xs={12} px={2} pb={2}>
                <SectionDivider label="Presentation från FairTransport" icon={<icons.fairtransport style={{ width: 20, height: 20, color: 'grey' }} />} marginTop={2} marginBottom={0} />
                <Box sx={{ px: 2, mt: 1, mb: 0 }}>
                    {entity.fairtransportDescription || 'Ingen beskrivning skrapad.'}
                </Box>
            </Grid>
        </Block>
    );

    const MetadataForCarriersBlock = (
        <>
            <Grid item xs={12} xl={6} px={2} pb={2}>
                <SectionDivider label={`Fordonstyper (${entity.vehicleTypes?.length || 0})`} icon={<LocalShipping fontSize="small" />} marginTop={0} marginBottom={0} />
                <Box component="ul" sx={{ px: 2, mt: 0, mb: 0 }}>
                    {sortBy(entity.vehicleTypes)
                        .map((veh) => (
                            <Box component="li" key={veh}>
                                {veh}
                            </Box>
                        ))}
                </Box>
            </Grid>
            <Grid item xs={12} xl={6} px={2} pb={2}>
                <SectionDivider label={`Utrustning (${entity.equipment?.length || 0})`} icon={<Construction fontSize="small" />} marginTop={0} marginBottom={0} />
                <Box component="ul" sx={{ px: 2, mt: 1, mb: 0 }}>
                    {sortBy(entity.equipment)
                        .map((eq) => (
                            <Box component="li" key={eq}>
                                {eq}
                            </Box>
                        ))}
                </Box>
            </Grid>
            <Grid item xs={12} xl={6} px={2} pb={2}>
                <SectionDivider label={`Hållbarhet (${entity.sustainability?.length ?? 0})`} icon={<Forest fontSize="small" />} marginTop={0} marginBottom={0} />
                <Box component="ul" sx={{ px: 2, mt: 1, mb: 0 }}>
                    {sortBy(entity.sustainability)
                        .map((sus) => (
                            <Box component="li" key={sus}>
                                {sus}
                            </Box>
                        ))}
                </Box>
            </Grid>
            <Grid item xs={12} xl={6} px={2} pb={2}>
                <SectionDivider label={`Lic./Cert. (${licenceCertificatesCount})`} icon={<CardMembership fontSize="small" />} marginTop={0} marginBottom={0} />
                <Box component="ul" sx={{ px: 2, mt: 1, mb: 0 }}>
                    {sortBy(allLicencesCertificates, [
                        (lic) => (lic.match(/\d+/) ? parseInt(lic.match(/\d+/)![0], 10) : Infinity),
                        (lic) => lic
                    ]).map((lic) => (
                        <Box component="li" key={lic}>
                            {lic}
                        </Box>
                    ))}
                </Box>
            </Grid>
            <Grid item xs={12} xl={6} px={2} pb={2}>
                <SectionDivider
                    label={`Täckning och rutter (${entity.routes?.length || 0})`}
                    icon={<MapIcon fontSize="small" />}
                    marginTop={0}
                    marginBottom={0}
                />
                <Box component="ul" sx={{ px: 2, mt: 1, mb: 0 }}>
                    {(entity.routes || []).map((route) => (
                        <Box component="li" key={route.id}>
                            {route.name}
                        </Box>
                    ))}
                </Box>
            </Grid>
        </>
    );

    const IndustryBlock = (
        <>
            <Grid item xs={12} xl={6} px={2} pb={2}>
                <SectionDivider label={`SNI-brancher (${entity.sniIndustries?.length ?? 0})`} icon={<CorporateFare fontSize="small" />} marginTop={0} marginBottom={0} />
                <Box component="ul" sx={{ px: 2, mt: 1, mb: 0 }}>
                    {sortBy(entity.sniIndustries)
                        .map((sni) => (
                            <Box component="li" key={sni}>
                                {sni}
                            </Box>
                        ))}
                </Box>
            </Grid>
            <Grid item xs={12} xl={6} px={2} pb={2}>
                <SectionDivider label={`Brancher (${entity.industries?.length ?? 0})`} icon={<Domain fontSize="small" />} marginTop={0} marginBottom={0} />
                <Box component="ul" sx={{ px: 2, mt: 1, mb: 0 }}>
                    {sortBy(entity.industries)
                        .map((ind) => (
                            <Box component="li" key={ind}>
                                {ind}
                            </Box>
                        ))}
                </Box>
            </Grid>
        </>
    );

    const scrapedJob = scrapedItem ? jobLabel(scrapedItem) : null;

    return (
        <Grid container spacing={2}>
            <Grid item xs={12} lg={12}>
                {!entity.isRejected && (
                    <Block title="Skrapad data" variant="outlined">
                        <Stack direction="row" spacing={1} sx={{ px: 2, pt: 1 }} flexWrap="wrap">
                            <Button variant="outlined" onClick={queueScrape}>Skrapa företaget för ny data</Button>
                            {scrapedItem && (
                                <>
                                    <Button variant="contained" onClick={() => setConfirmMode('overwrite')}>
                                        Uppdatera alla fält
                                    </Button>
                                    <Button variant="outlined" onClick={() => setConfirmMode('unedited')}>
                                        Uppdatera bara orörda fält
                                    </Button>
                                </>
                            )}
                        </Stack>
                        <Box sx={{ px: 2, mt: 1 }}>
                            {!scrapedItem || !scrapedItem.detailScrapedAt
                                ? 'Inte skrapad'
                                : <ScrapedChanges changes={scrapedItem.changes} />}
                            {scrapedJob && <Box>{scrapedJob}</Box>}
                        </Box>
                        <Popup
                            open={confirmMode != null}
                            title="Uppdatera från skrapad data"
                            body="Vill du uppdatera 1 företag?"
                            okLabel="Uppdatera"
                            handleClose={() => setConfirmMode(null)}
                            handleOk={async () => {
                                if (!confirmMode) {
                                    return;
                                }
                                try {
                                    const result = await applyScrapedCompanies({
                                        tenantIds: [entity.id],
                                        scope: 'selected',
                                        mode: confirmMode,
                                        acceptedOnly: null,
                                        failedOnly: null,
                                        hasChanges: null
                                    });
                                    toast(`Uppdaterade ${result.updated}, överhoppade ${result.skipped}`);
                                    setConfirmMode(null);
                                    fetch();
                                    loadScraped();
                                } catch (error) {
                                    toast(error, 'Kunde inte uppdatera');
                                }
                            }}
                        />
                    </Block>
                )}
                {BasicInfoBlock}
                <Grid container spacing={0} sx={{ py: 1 }} />
                {entity.isCarrier && (
                    <>
                        {ScrapedDataBlock}
                        <Grid container spacing={0} sx={{ py: 1 }} />
                    </>
                )}
                <TenantRecommendations tenantId={entity.id} />
                <Grid container spacing={0} sx={{ py: 1 }} />
                <Block
                    title="Metadata"
                    variant="outlined"
                >
                    <Grid container spacing={0} sx={{ mt: 1, px: 2 }}>
                        {entity.isCarrier && (
                            MetadataForCarriersBlock
                        )}
                        {IndustryBlock}
                    </Grid>
                </Block>
            </Grid>
            {/* {TenantMapBlock} */}
        </Grid>
    );
}

export default function TenantDetailsFetched({ hasRendered }: { hasRendered?: boolean }) {
    const { id } = useParams() as unknown as { id: number };

    return (
        <FetchRedux<TenantType>
            fetchEntity={fetchTenantById(id)}
            selectEntity={selectTenantById(id)}
            selectIsFetching={selectFetchingTenantById(id)}
            Component={TenantDetails}
            name="företaget"
            initialFetch={!hasRendered}
        />
    );
}
